import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { walletLimitsSchema } from '../../../schemas/wallet/admin.schema';
import { Pencil, Plus } from 'lucide-react';
import DataTable from '../../../component/wallet/data-table/DataTable';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import StatusBadge from '../../../component/wallet/StatusBadge';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { getCurrencies } from '../../../api/wallet/wallet.api';

const columnHelper = createColumnHelper();

const WALLET_TYPE_LABELS = {
    real_money: 'Real Money',
    reward: 'Reward',
};

const WalletLimitsPage = () => {
    const { sidebar } = useContext(AppContext);
    const { loading, fetchLimits, doSetLimits } = useAdmin();
    const [limits, setLimits] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: zodResolver(walletLimitsSchema),
    });

    // Returns data only — state is set by the caller so useEffect stays clean
    const loadLimits = useCallback(async () => {
        const res = await fetchLimits();
        return res?.data || [];
    }, [fetchLimits]);

    useEffect(() => {
        Promise.all([loadLimits(), getCurrencies()])
            .then(([limitsData, cRes]) => {
                setLimits(limitsData);
                setCurrencies(cRes?.data || []);
            })
            .catch(() => {});
    }, [loadLimits]);

    const handleAdd = useCallback(() => {
        setIsAdding(true);
        reset({ is_active: true });
        setShowModal(true);
    }, [reset]);

    const handleEdit = useCallback((limit) => {
        setIsAdding(false);
        reset({
            currency_code: limit.currency_code,
            wallet_type: limit.wallet_type,
            max_balance: limit.max_balance,
            min_transaction: limit.min_transaction,
            max_transaction: limit.max_transaction,
            daily_transaction_limit: limit.daily_transaction_limit,
            is_active: limit.is_active,
        });
        setShowModal(true);
    }, [reset]);

    const handleClose = useCallback(() => {
        setShowModal(false);
        reset();
    }, [reset]);

    const onSubmit = async (data) => {
        try {
            await doSetLimits(data);
            handleClose();
            loadLimits().then(setLimits).catch(() => {});
        } catch {
            // handled by hook
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('currency_code', { header: 'Currency' }),
            columnHelper.accessor('wallet_type', {
                header: 'Wallet Type',
                cell: (info) => WALLET_TYPE_LABELS[info.getValue()] ?? info.getValue(),
            }),
            columnHelper.accessor('max_balance', {
                header: 'Max Balance',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('min_transaction', {
                header: 'Min Transaction',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('max_transaction', {
                header: 'Max Transaction',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('daily_transaction_limit', {
                header: 'Daily Limit',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('is_active', {
                header: 'Status',
                cell: (info) => <StatusBadge status={info.getValue() ? 'active' : 'inactive'} />,
            }),
            columnHelper.display({
                id: 'actions',
                header: '',
                cell: ({ row }) => (
                    <Button size="sm" variant="outline-primary" onClick={() => handleEdit(row.original)}>
                        <Pencil size={14} />
                    </Button>
                ),
            }),
        ],
        [handleEdit]
    );

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Wallet Limits</h6>
            </div>
                <div className="fade-in">
            <div className="mb-3 text-end">
                <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
                    Add Limit
                </Button>
            </div>

            <DataTable
                columns={columns}
                data={limits}
                loading={loading}
                emptyTitle="No limits configured"
                emptyDescription="Configure wallet limits to control transaction boundaries."
            />

            {/* Add / Edit Modal */}
            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isAdding ? 'Add Wallet Limit' : 'Edit Wallet Limit'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Currency</Form.Label>
                            {isAdding ? (
                                <Form.Select isInvalid={!!errors.currency_code} {...register('currency_code')}>
                                    <option value="">Select currency</option>
                                    {currencies.map((c) => (
                                        <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <Form.Control type="text" readOnly {...register('currency_code')} />
                            )}
                            <Form.Control.Feedback type="invalid">{errors.currency_code?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Wallet Type</Form.Label>
                            <Form.Select isInvalid={!!errors.wallet_type} {...register('wallet_type')}>
                                <option value="">Select wallet type</option>
                                <option value="real_money">Real Money</option>
                                <option value="reward">Reward</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{errors.wallet_type?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Max Balance</Form.Label>
                            <Form.Control type="number" step="0.01" isInvalid={!!errors.max_balance} {...register('max_balance')} />
                            <Form.Control.Feedback type="invalid">{errors.max_balance?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Min Transaction</Form.Label>
                            <Form.Control type="number" step="0.01" isInvalid={!!errors.min_transaction} {...register('min_transaction')} />
                            <Form.Control.Feedback type="invalid">{errors.min_transaction?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Max Transaction</Form.Label>
                            <Form.Control type="number" step="0.01" isInvalid={!!errors.max_transaction} {...register('max_transaction')} />
                            <Form.Control.Feedback type="invalid">{errors.max_transaction?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Daily Transaction Limit</Form.Label>
                            <Form.Control type="number" step="0.01" isInvalid={!!errors.daily_transaction_limit} {...register('daily_transaction_limit')} />
                            <Form.Control.Feedback type="invalid">{errors.daily_transaction_limit?.message}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Check type="switch" label="Active" {...register('is_active')} className="mb-3" />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : isAdding ? 'Add' : 'Save'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
        </div>
        </div>
    );
};

export default WalletLimitsPage;
